/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	
	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const productLog = (level, message, data = {}) => {
	if (!PRODUCT_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🛍️ [PRODUCT] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Product Event Emitter
const productEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'product'
	};
	
	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;
		
		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			productLog('info', 'Variant ID updated', { 
				oldVariantId: oldValue, 
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		
		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;
		
		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID first, then by specific class, fallback to index
			let newElement = null;
			
			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.className) {
				// Match by specific component classes for better targeting
				const classList = Array.from(currentElement.classList);
				const specificClass = classList.find(cls => 
					cls.includes('component-price') || 
					cls.includes('component-quantity') || 
					cls.includes('component-cta') ||
					cls.includes('sticky-cta')
				);
				
				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0];
				}
			}
			
			// Fallback to index-based matching
			if (!newElement) {
				newElement = newElements[index];
			}
			
			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
					productEmit('product-reload-element-updated', { 
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					productLog('warn', 'Element update failed', { 
						index, 
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message 
					});
				}
			}
		});

		productLog('info', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});
		
		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;

		// Size is "explicitly selected" only if URL has ?variant= without no_size=1
		const _urlParams = new URLSearchParams(window.location.search);
		this.sizeExplicitlySelected = _urlParams.has('variant') && _urlParams.get('no_size') !== '1';

		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm);
		if (!this.container) {
			productLog('error', 'VariantSelector: No product form container found');
			return;
		}

		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		productEmit('variant-selector-init-start');
		
		try {
			this.addEventListener("change", this.onVariantChange);
			this.getSelectedOptions();

			this.getSelectedVariant();
			
			productEmit('variant-selector-init-success');
		} catch (error) {
			productLog('error', 'Failed to initialize VariantSelector', { error: error.message });
		}
	}

	onVariantChange(event) {
		productEmit('variant-change-start');

		// Capture before setTimeout: size is explicit only when user clicks a size in the modal
		// (which dispatches a bubbling change event on the hidden select)
		const isExplicitSizeChange = event?.target?.matches('select[data-variant-input]');

		try {
			setTimeout(() => {
				if (isExplicitSizeChange) {
					this.sizeExplicitlySelected = true;
				}

				this.getSelectedOptions();
				this.getSelectedVariant();

				if (this.currentVariant) {
					productEmit('variant-found', {
						variantId: this.currentVariant.id,
						available: this.currentVariant.available
					});

					// Orchestrate all updates
					this.updateURL();
					this.updateSelectedColorDisplay();
					this.updateProductData();
					this.updateMediaDisplay();
				} else {
					productLog('warn', 'No matching variant found for options', { options: this.options });
					productEmit('variant-not-found', { options: this.options });
					this.selectFirstAvailableSizeForColor();
				}
			}, 10);
		} catch (error) {
			productLog('error', 'Error in variant change', { error: error.message });
		}
	}

	/**
	 * Update displayed color value when variant changes
	 */
	updateSelectedColorDisplay() {
		const displayEl = this.querySelector('[data-selected-color-value]');
		if (displayEl && this.options[0]) {
			displayEl.textContent = this.options[0];
		}
	}

	/**
	 * When color changes but current size doesn't exist for the new color:
	 * auto-select the first available size, then trigger the full variant update.
	 */
	selectFirstAvailableSizeForColor() {
		const variants = this.getVariantJSON();
		const selectedColor = this.options[0];
		if (!selectedColor || !variants.length) return;

		const colorVariants = variants.filter(v => v.options[0] === selectedColor);
		const targetVariant = colorVariants.find(v => v.available) || colorVariants[0];
		if (!targetVariant) return;

		// Update each non-color (size) hidden select to match the target variant
		let changed = false;
		this.querySelectorAll('select[data-variant-input][data-option-position]').forEach(select => {
			const position = parseInt(select.dataset.optionPosition, 10);
			if (position === 1) return; // skip color option
			const targetValue = targetVariant.options[position - 1];
			if (targetValue !== undefined && select.value !== targetValue) {
				select.value = targetValue;
				// Sync the visual trigger label without re-triggering onVariantChange
				select.dispatchEvent(new Event('change', { bubbles: false }));
				changed = true;
			}
		});

		if (!changed) return;

		// Re-read options with the updated size and find the variant
		this.getSelectedOptions();
		this.getSelectedVariant();

		if (this.currentVariant) {
			productEmit('variant-found', {
				variantId: this.currentVariant.id,
				available: this.currentVariant.available
			});
			this.updateURL(true); // size was auto-selected, not explicitly chosen
			this.updateSelectedColorDisplay();
			this.updateProductData();
			this.updateMediaDisplay();
		} else {
			// Fallback: the size option is absent from the select DOM (e.g. hide_if_oos metafield
			// hides it even when the product has only one size like "Taille unique").
			// Use targetVariant directly since we already identified the correct one for this color.
			this.currentVariant = targetVariant;
			this.options = [...targetVariant.options];
			productEmit('variant-found', {
				variantId: this.currentVariant.id,
				available: this.currentVariant.available
			});
			this.updateURL(true);
			this.updateSelectedColorDisplay();
			this.updateProductData();
			this.updateMediaDisplay();
		}
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);
		
		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);
		}
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			productLog('warn', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;
		
		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;
		
		if (!sectionId) {
			productLog('warn', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

		// Check cache first
		if (this.cached.has(variantUrl)) {
			return this.cached.get(variantUrl);
		}

		try {
			const response = await fetch(variantUrl);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			
			const responseText = await response.text();
			const html = new DOMParser().parseFromString(responseText, 'text/html');
			
			// Cache the result
			this.cached.set(variantUrl, html);
			
			productEmit('variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			productLog('error', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options from variant inputs (radios, checkboxes, select)
	 */
	getSelectedOptions() {
		const results = [];
		this.querySelectorAll('[data-variant-value]').forEach(el => {
			const input = el.querySelector('input[data-variant-input]');
			const select = el.querySelector('select[data-variant-input]');
			if (input && input.checked) results.push(input.value);
			else if (select && select.value) results.push(select.value);
		});
		this.options = results.filter(Boolean);
	}

	/**
	 * Get the selected variant based on options
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();
		
		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		this.currentVariant = variants.find(variant => {
			if (!variant.options || !Array.isArray(variant.options)) return false;
			return variant.options.every((option, index) => this.options[index] === option);
		});
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('[type="application/json"]');
			
			if (!jsonElement) {
				productLog('error', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}
			
			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				productLog('error', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected variant
	 * @param {boolean} forceNoSize - true when size was auto-selected (overrides sizeExplicitlySelected)
	 */
	updateURL(forceNoSize = false) {
		if (!this.currentVariant?.id) return;

		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', this.currentVariant.id);
			if (forceNoSize || !this.sizeExplicitlySelected) {
				url.searchParams.set('no_size', '1');
			} else {
				url.searchParams.delete('no_size');
			}
			// Préserver ?outlet= pour maintenir le contexte outlet lors des changements de variant
			const outletParam = new URLSearchParams(window.location.search).get('outlet');
			if (outletParam) url.searchParams.set('outlet', outletParam);
			if (window.location.hash) url.hash = window.location.hash;
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			productLog('error', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		const currentOptions = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		const newOptions = html.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		
		if (!newOptions.length) return;

		currentOptions.forEach((currentOption, index) => {
			const newOption = newOptions[index];
			if (!newOption) return;

			const currentInput = currentOption.querySelector('input, select');
			const newInput = newOption.querySelector('input, select');
			
			if (currentInput && newInput) {
				currentOption.className = newOption.className;
				
				if (currentInput.tagName === 'SELECT' && newInput.tagName === 'SELECT') {
					// Select: sync value and option disabled states
					const currentValue = currentInput.value;
					currentInput.innerHTML = newInput.innerHTML;
					currentInput.value = currentValue || newInput.value;
					Array.from(newInput.options).forEach((opt, i) => {
						const currOpt = currentInput.options[i];
						if (currOpt) currOpt.disabled = opt.disabled;
					});
				} else if (currentInput.tagName === 'INPUT' && newInput.tagName === 'INPUT') {
					// Radio/checkbox: update disabled, preserve checked
					currentInput.disabled = newInput.disabled;
					const currentlyChecked = currentInput.checked;
					if (currentlyChecked !== newInput.checked) {
						currentInput.checked = currentlyChecked;
					}
				}
			}
		});
	}
}

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	productEmit('quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		// Update price elements
		ProductDOM.priceElements.forEach(priceElement => {
			const priceValue = parseFloat(priceElement.dataset.price);
			if (!isNaN(priceValue)) {
				const totalPrice = (priceValue * quantity) / 100;
				const formattedPrice = new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR'
				}).format(totalPrice);
				priceElement.textContent = formattedPrice;
				updatedElementsCount++;
			}
		});

		productEmit('quantity-update-success', { 
			quantity, 
			updatedElements: updatedElementsCount 
		});
		
		productLog('info', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		productLog('error', 'Failed to update prices', { 
			quantity, 
			error: error.message 
		});
		productEmit('quantity-update-error', { 
			quantity, 
			error: error.message 
		});
	}
}

/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Initialize sticky CTA behavior
 */
let _stickyCTAObserverRefs = [];

function updateStickyCTAVisibility(classicVisible, footerVisible) {
	const shouldHide = classicVisible || footerVisible;
	document.querySelectorAll('[data-sticky-cta]').forEach((el) => {
		el.classList.toggle('is-visible', !shouldHide);
		if (shouldHide) {
			el.setAttribute('inert', '');
			el.setAttribute('aria-hidden', 'true');
		} else {
			el.removeAttribute('inert');
			el.setAttribute('aria-hidden', 'false');
		}
	});
}

function initializeStickyCTA() {
	const classicCTA = document.querySelector('#product-form > .mm-product-add-to-cart-container[data-cta]');
	const footer = document.querySelector('footer');
	const stickyCTAs = document.querySelectorAll('[data-sticky-cta]');
	if (!classicCTA || !stickyCTAs.length) {
		productLog('warn', 'StickyCTA: Required elements not found');
		return;
	}

	try {
		_stickyCTAObserverRefs.forEach((ref) => ref.disconnect());
		_stickyCTAObserverRefs = [];

		let classicVisible = true;
		let footerVisible = false;
		updateStickyCTAVisibility(classicVisible, footerVisible);

		const applyVisibility = () => updateStickyCTAVisibility(classicVisible, footerVisible);

		const classicObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				const rect = entry.boundingClientRect;
				// Afficher le sticky uniquement quand le CTA est EN DESSOUS (scrollé passé), pas quand il est au-dessus (pas encore atteint)
				if (entry.isIntersecting) {
					classicVisible = true;
				} else {
					// CTA hors viewport : rect.top > innerHeight = CTA en dessous (pas atteint) → cacher
					// rect.bottom < 0 = CTA au-dessus (scrollé passé) → afficher
					classicVisible = rect.top > window.innerHeight;
				}
				applyVisibility();
			});
		}, { threshold: 0, rootMargin: '0px 0px -10px 0px' });

		classicObserver.observe(classicCTA);
		_stickyCTAObserverRefs.push(classicObserver);

		if (footer) {
			const footerObserver = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					footerVisible = entry.isIntersecting;
					applyVisibility();
				});
			}, { threshold: 0 });

			footerObserver.observe(footer);
			_stickyCTAObserverRefs.push(footerObserver);
		}
	} catch (error) {
		productLog('error', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/** Client Back In Stock (CORS OK) — ne pas utiliser /api/v1/catalog/subscribe depuis le navigateur */
const KLAVIYO_BIS_API_REVISION = '2026-01-15';

function buildKlaviyoClientBisSubscribeUrl(companyId) {
	const u = new URL('https://a.klaviyo.com/client/back-in-stock-subscriptions');
	u.searchParams.set('company_id', companyId);
	return u.toString();
}

/** ID catalogue Klaviyo pour une variante Shopify (doc Klaviyo client BIS) */
function klaviyoShopifyCatalogVariantId(shopifyVariantId) {
	return `$shopify:::$default:::${shopifyVariantId}`;
}

function initKlaviyoBackInStock() {
	function getSoldOutModal() {
		return document.querySelector('[data-modal="sold-out"]');
	}

	function getCurrentBisVariantId(modal) {
		const pending = modal?.getAttribute('data-bis-variant-id');
		if (pending) return pending.trim();
		const input = document.querySelector('[data-selected-variant-id]');
		return input?.value?.trim() || '';
	}

	function validateBisEmail(email) {
		return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
			String(email).toLowerCase()
		);
	}

	function resetKlaviyoBisForm() {
		const modal = getSoldOutModal();
		if (!modal) return;
		const root = modal.querySelector('[data-klaviyo-bis-root]');
		if (!root) return;

		root.querySelector('[data-klaviyo-bis-form]')?.removeAttribute('hidden');
		root.querySelector('[data-klaviyo-bis-success]')?.setAttribute('hidden', '');
		root.querySelector('[data-klaviyo-bis-error-email]')?.setAttribute('hidden', '');
		root.querySelector('[data-klaviyo-bis-error-generic]')?.setAttribute('hidden', '');

		const emailEl = root.querySelector('[data-klaviyo-bis-email]');
		if (emailEl) emailEl.value = '';

		const submitBtn = root.querySelector('[data-klaviyo-bis-submit]');
		if (submitBtn) {
			submitBtn.disabled = false;
			submitBtn.removeAttribute('aria-busy');
		}
	}

	document.addEventListener('modal:opened', (e) => {
		if (e.detail?.id === 'sold-out') resetKlaviyoBisForm();
	});

	document.addEventListener('modal:closed', (e) => {
		if (e.detail?.id !== 'sold-out') return;
		getSoldOutModal()?.removeAttribute('data-bis-variant-id');
	});

	document.addEventListener(
		'submit',
		(e) => {
			const form = e.target.closest('[data-klaviyo-bis-form-element]');
			if (!form) return;

			e.preventDefault();

			const modal = getSoldOutModal();
			if (!modal) return;

			const root = modal.querySelector('[data-klaviyo-bis-root]');
			if (!root) return;

			const publicKey = root.dataset.klaviyoPublicKey;
			if (!publicKey) return;

			const emailInput = root.querySelector('[data-klaviyo-bis-email]');
			const submitBtn = root.querySelector('[data-klaviyo-bis-submit]');
			const errEmail = root.querySelector('[data-klaviyo-bis-error-email]');
			const errGeneric = root.querySelector('[data-klaviyo-bis-error-generic]');
			const successEl = root.querySelector('[data-klaviyo-bis-success]');
			const formWrap = root.querySelector('[data-klaviyo-bis-form]');

			errEmail?.setAttribute('hidden', '');
			errGeneric?.setAttribute('hidden', '');

			const email = emailInput?.value?.trim() || '';
			if (!validateBisEmail(email)) {
				errEmail?.removeAttribute('hidden');
				return;
			}

			const variantId = getCurrentBisVariantId(modal);
			if (!variantId) {
				errGeneric?.removeAttribute('hidden');
				return;
			}

			if (submitBtn) {
				submitBtn.disabled = true;
				submitBtn.setAttribute('aria-busy', 'true');
			}

			const catalogVariantId = klaviyoShopifyCatalogVariantId(variantId);
			const payload = {
				data: {
					type: 'back-in-stock-subscription',
					attributes: {
						channels: ['EMAIL'],
						profile: {
							data: {
								type: 'profile',
								attributes: { email }
							}
						}
					},
					relationships: {
						variant: {
							data: {
								type: 'catalog-variant',
								id: catalogVariantId
							}
						}
					}
				}
			};

			fetch(buildKlaviyoClientBisSubscribeUrl(publicKey), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/vnd.api+json',
					Accept: 'application/vnd.api+json',
					revision: KLAVIYO_BIS_API_REVISION
				},
				body: JSON.stringify(payload)
			})
				.then(async (response) => {
					if (response.status === 202 || response.ok) {
						formWrap?.setAttribute('hidden', '');
						successEl?.removeAttribute('hidden');
						return;
					}
					errGeneric?.removeAttribute('hidden');
				})
				.catch(() => {
					errGeneric?.removeAttribute('hidden');
				})
				.finally(() => {
					if (submitBtn) {
						submitBtn.disabled = false;
						submitBtn.removeAttribute('aria-busy');
					}
				});
		},
		true
	);
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom element
customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		// Initialize components
		initializeStickyCTA();

		// Clic sur le CTA principal "Être prévenu" : copier le titre depuis data-sold-out-title (variante sélectionnée)
		document.addEventListener('click', (e) => {
			const cta = e.target.closest('.mm-product-add-to-cart-sold-out-cta');
			if (cta && cta.dataset.soldOutTitle) {
				const soldOutModal = document.querySelector('[data-modal="sold-out"]');
				soldOutModal?.removeAttribute('data-bis-variant-id');
				const titleEl = soldOutModal?.querySelector('[data-sold-out-title]');
				if (titleEl) titleEl.textContent = cta.dataset.soldOutTitle;
			}
		}, true);

		initKlaviyoBackInStock();

		productEmit('initialized');
		productLog('info', 'Product page initialized successfully');
	} catch (error) {
		productLog('error', 'Failed to initialize product page', { error: error.message });
	}
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */

document.addEventListener('DOMContentLoaded', () => {
	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
			}

			// Update button states after variant change
			updateQuantityButtonStates(input);
		});

		// Update price for the first input found (there should be only one on product page)
		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			mmUpdatePrice(newValue);
		}

		// Réinitialiser le sticky CTA (éléments CTA remplacés après changement de variante)
		initializeStickyCTA();
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (PRODUCT_CONFIG.isDevelopment) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});

}